import { API_ENDPOINTS, resolveImageUrl } from '../config/api';
import {
  normalizePoojaApiLang,
  LOCALIZED_FALLBACK_ORDER,
  getLocaleForLang,
  LANG_CODES,
  localizedLangKeys,
  normalizeUiLang,
} from '../config/languages';
import { apiFetch } from './api';

export const POOJAS_API = API_ENDPOINTS.poojas;

/** Backend `GET /api/poojas/website?type=...` values */
export const POOJA_SCHEDULE_TYPES = ['PAST', 'LIVE', 'UPCOMING'];

function normalizePoojaListLang(lang) {
  return normalizePoojaApiLang(lang);
}

function normalizeScheduleType(scheduleType) {
  if (scheduleType == null || scheduleType === '') return '';
  const u = String(scheduleType).toUpperCase();
  return POOJA_SCHEDULE_TYPES.includes(u) ? u : '';
}

export function normalizePoojaScheduleType(scheduleType, fallback = 'LIVE') {
  return normalizeScheduleType(scheduleType) || normalizeScheduleType(fallback) || 'LIVE';
}

/** API: browseable poojas only when `status` is strictly `true`. */
export function isPoojaActiveStatus(pooja) {
  return pooja?.status === true;
}

let poojasCacheByKey = {};
let poojasRequestByKey = {};

function unwrapPoojaListResponse(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.data?.content)) return json.data.content;
  if (Array.isArray(json?.content)) return json.content;
  return null;
}

/** Website catalog channel for `GET /api/poojas/website` (desktop). */
export const POOJA_WEBSITE_CHANNEL = 'website';
export const POOJA_MOBILE_CHANNEL = 'mobile';

let poojaChannelMemo = null;

/**
 * `mobile` for phones and tablets, `website` otherwise. Device-based (not window width): the viewport
 * is not settled when the index.html prefetch runs, and both must agree or the early response is wasted.
 * Keep the rule identical to the inline script in public/index.html.
 */
export function getPoojaChannel() {
  if (poojaChannelMemo) return poojaChannelMemo;
  if (typeof navigator === 'undefined') return POOJA_WEBSITE_CHANNEL;
  const uad = navigator.userAgentData;
  const mobile = uad && typeof uad.mobile === 'boolean'
    ? uad.mobile
    : /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
  poojaChannelMemo = mobile ? POOJA_MOBILE_CHANNEL : POOJA_WEBSITE_CHANNEL;
  return poojaChannelMemo;
}

/**
 * Fetch poojas list. Passes `?lang=` from the navbar selection (all codes in `LANG_CODES` from `config/languages`).
 * Optional `scheduleType`: `PAST` | `LIVE` | `UPCOMING` → `GET /api/poojas/website?type=...&lang=...&channel=website`
 * Without `scheduleType` → `GET /api/poojas/users?lang=...` (full list for favorites / legacy).
 * Cached per language + type; switching language or type refetches.
 */
/** Request started from index.html (see public/index.html) — handed over once, then removed. */
function takeEarlyApiResponse(url) {
  try {
    const early = typeof window !== 'undefined' ? window.__DAIV_EARLY_API__ : null;
    const pending = early && early[url];
    if (!pending) return null;
    delete early[url];
    return pending;
  } catch {
    return null;
  }
}

export function getPoojas(forceRefresh = false, lang, scheduleType) {
  const cacheKey = normalizePoojaListLang(lang);
  const st = normalizeScheduleType(scheduleType);
  const storageKey = `${cacheKey}|${st || 'all'}|${st ? getPoojaChannel() : ''}`;

  if (forceRefresh) {
    poojasCacheByKey = {};
    poojasRequestByKey = {};
  }

  if (poojasCacheByKey[storageKey]) {
    return Promise.resolve(poojasCacheByKey[storageKey]);
  }

  if (poojasRequestByKey[storageKey]) {
    return poojasRequestByKey[storageKey];
  }

  const langQ = `lang=${encodeURIComponent(cacheKey)}`;
  const listUrl = st
    ? `${API_ENDPOINTS.poojasBySchedule}?${langQ}&type=${encodeURIComponent(st)}&channel=${encodeURIComponent(getPoojaChannel())}`
    : `${POOJAS_API}?${langQ}`;

  const earlyResponse = takeEarlyApiResponse(listUrl);
  const responsePromise = earlyResponse
    ? earlyResponse
        .then((res) => (res.ok ? res : Promise.reject(new Error('early request failed'))))
        .catch(() => apiFetch(listUrl, { skipAuth: true }))
    : apiFetch(listUrl, { skipAuth: true });

  poojasRequestByKey[storageKey] = responsePromise
    .then((res) => {
      if (!res.ok) {
        throw new Error('Failed to load poojas');
      }
      return res.json();
    })
    .then((json) => {
      const data = unwrapPoojaListResponse(json);
      if (!data) {
        throw new Error(json?.message || 'Failed to load poojas');
      }
      const activeList = data.filter(isPoojaActiveStatus);
      poojasCacheByKey[storageKey] = activeList;
      return activeList;
    })
    .catch((err) => {
      poojasRequestByKey[storageKey] = null;
      throw err;
    });

  return poojasRequestByKey[storageKey];
}

export const POOJA_PAGE_SIZE = 20;

/** Parse paginated pooja list payload (+ legacy array), same shape as daiv blogs. */
export function extractPoojaPage(json) {
  const data = json?.data ?? json;
  if (Array.isArray(data)) {
    const content = data.filter(isPoojaActiveStatus);
    return {
      content,
      page: 0,
      size: data.length,
      totalPages: 1,
      totalElements: content.length,
    };
  }
  const rawContent = Array.isArray(data?.content)
    ? data.content
    : Array.isArray(json?.content)
      ? json.content
      : [];
  const content = rawContent.filter(isPoojaActiveStatus);
  const page = Number.isFinite(Number(data?.page ?? json?.page)) ? Number(data?.page ?? json?.page) : 0;
  const size = Number.isFinite(Number(data?.size ?? json?.size)) ? Number(data?.size ?? json?.size) : POOJA_PAGE_SIZE;
  const totalPages = Number.isFinite(Number(data?.totalPages ?? json?.totalPages))
    ? Number(data?.totalPages ?? json?.totalPages)
    : content.length > 0
      ? 1
      : 0;
  const totalElements = Number.isFinite(Number(data?.totalElements ?? json?.totalElements))
    ? Number(data?.totalElements ?? json?.totalElements)
    : content.length;
  return { content, page, size, totalPages, totalElements };
}

export function mergePoojaPages(existing, incoming) {
  const seen = new Set();
  const out = [];
  for (const pooja of [...(existing || []), ...(incoming || [])]) {
    const key = pooja?.id || pooja?.slug;
    if (key == null || key === '' || seen.has(String(key))) continue;
    seen.add(String(key));
    out.push(pooja);
  }
  return out;
}

/**
 * Paginated `GET /api/poojas/website?type=&lang=&page=&size=&channel=website` (blogs-style infinite scroll).
 * Does not use the full-list `getPoojas` cache.
 */
export function getPoojasPage({ page = 0, size = POOJA_PAGE_SIZE, lang, scheduleType } = {}) {
  const cacheKey = normalizePoojaListLang(lang);
  const st = normalizeScheduleType(scheduleType);
  const params = new URLSearchParams({
    lang: cacheKey,
    page: String(page),
    size: String(size),
  });
  if (st) {
    params.set('type', st);
    params.set('channel', getPoojaChannel());
  }
  const listUrl = st
    ? `${API_ENDPOINTS.poojasBySchedule}?${params.toString()}`
    : `${POOJAS_API}?${params.toString()}`;

  return apiFetch(listUrl, { skipAuth: true })
    .then((res) => {
      if (!res.ok) {
        throw new Error('Failed to load poojas');
      }
      return res.json();
    })
    .then((json) => extractPoojaPage(json));
}

function unwrapPoojaDetailsResponse(json) {
  const data = json?.data ?? json;
  const item = Array.isArray(data) ? data[0] : data;
  if (!item || typeof item !== 'object' || !(item.id || item.slug)) {
    throw new Error(json?.message || 'Invalid pooja details response');
  }
  if (!isPoojaActiveStatus(item)) throw new Error('Pooja not found');
  return item;
}

/** Fields from `/api/poojas/details` or `/api/poojas/{id}` that list cards often omit. */
const POOJA_DETAIL_RICH_KEYS = [
  'blocks',
  'description',
  'faq',
  'faqs',
  'divineBenefits',
  'whatsIncluded',
  'whats_included',
  'prasadaIncludedItems',
  'prasadamIncludedItems',
  'prasada_included_items',
  'prasadam_included_items',
  'poojaCoordinators',
  'poojaVideoLinks',
  'blockedDates',
  'blocked_dates',
  'bookingFields',
  'booking_fields',
  'aboutTitle',
  'megaPoojaDetails',
  'mega_pooja_details',
];

function isEmptyPoojaDetailField(value) {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'string') return !value.trim();
  if (typeof value === 'object') {
    return !Object.values(value).some((v) => {
      if (typeof v === 'string') return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      return v != null;
    });
  }
  return false;
}

function pickRicherPoojaDetailField(existing, incoming) {
  if (!isEmptyPoojaDetailField(incoming)) return incoming;
  if (!isEmptyPoojaDetailField(existing)) return existing;
  return incoming !== undefined ? incoming : existing;
}

/**
 * Fetch complete public pooja detail by slug + schedule.
 * New backend shape: `{ success: true, data: [pooja] }`.
 */
export function getPoojaDetailsBySlug(slug, scheduleType = 'LIVE', lang = 'en') {
  const apiSlug = slugifyForApi(slug);
  if (!apiSlug) return Promise.reject(new Error('Invalid pooja slug'));
  const st = normalizePoojaScheduleType(scheduleType, 'LIVE');
  const langQ = normalizePoojaListLang(lang);
  const rawSlug = String(slug || '').trim();

  const fetchBySlugParam = (slugParam) => {
    const detailsUrl = API_ENDPOINTS.poojaDetailsBySlug({
      slug: slugParam,
      scheduleType: st,
      lang: langQ,
    });
    /* index.html may already have started this exact request (campaign landing) — reuse it once. */
    const early = takeEarlyApiResponse(detailsUrl);
    const request = early
      ? early
          .then((res) => (res.ok ? res : Promise.reject(new Error('early request failed'))))
          .catch(() => apiFetch(detailsUrl, { skipAuth: true }))
      : apiFetch(detailsUrl, { skipAuth: true });
    return request
      .then((res) => {
        if (!res.ok) throw new Error('Pooja not found');
        return res.json();
      })
      .then(unwrapPoojaDetailsResponse);
  };

  return fetchBySlugParam(apiSlug).catch((err) => {
    if (rawSlug && rawSlug !== apiSlug) {
      return fetchBySlugParam(rawSlug).catch(() => Promise.reject(err));
    }
    return Promise.reject(err);
  });
}

/** Full pooja record by id — includes `blocks`, FAQ, etc. (list cards often omit these). */
export function getPoojaById(poojaId) {
  const id = String(poojaId || '').trim();
  if (!id) return Promise.reject(new Error('Invalid pooja id'));
  return apiFetch(API_ENDPOINTS.poojaById(id), { skipAuth: true })
    .then((res) => {
      if (!res.ok) throw new Error('Pooja not found');
      return res.json();
    })
    .then((json) => {
      const data = json?.data ?? json;
      if (!data || typeof data !== 'object' || !data.id) {
        throw new Error(json?.message || 'Invalid pooja details response');
      }
      if (!isPoojaActiveStatus(data)) throw new Error('Pooja not found');
      return data;
    });
}

/** Catalog `/website` lists often null out sheet fields; detail/users keep them. */
export function poojaNeedsCatalogSheetEnrichment(pooja, lang = 'en') {
  if (!pooja?.id) return false;
  const benefits = getLocalizedHtmlList(pooja?.divineBenefits, lang);
  const included = getLocalizedHtmlList(pooja?.whatsIncluded ?? pooja?.whats_included, lang);
  return benefits.length === 0 || included.length === 0;
}

const catalogSheetFieldsCache = Object.create(null);
const catalogSheetFieldsInflight = Object.create(null);

/**
 * Fetch Divine Benefits / What’s Included for catalog cards when list API omits them.
 * Cached per pooja id; safe to call from many visible cards.
 */
export function fetchPoojaCatalogSheetFields(poojaId) {
  const id = String(poojaId || '').trim();
  if (!id) return Promise.resolve(null);
  if (Object.prototype.hasOwnProperty.call(catalogSheetFieldsCache, id)) {
    return Promise.resolve(catalogSheetFieldsCache[id]);
  }
  if (catalogSheetFieldsInflight[id]) return catalogSheetFieldsInflight[id];

  catalogSheetFieldsInflight[id] = getPoojaById(id)
    .then((detail) => {
      const fields = {
        divineBenefits: detail?.divineBenefits ?? null,
        whatsIncluded: detail?.whatsIncluded ?? detail?.whats_included ?? null,
        whats_included: detail?.whats_included ?? detail?.whatsIncluded ?? null,
      };
      catalogSheetFieldsCache[id] = fields;
      return fields;
    })
    .catch(() => {
      catalogSheetFieldsCache[id] = null;
      return null;
    })
    .finally(() => {
      delete catalogSheetFieldsInflight[id];
    });

  return catalogSheetFieldsInflight[id];
}

/** True when pooja has About-section HTML (paragraph blocks or description). */
export function poojaHasAboutContent(pooja, lang = 'en') {
  if (!pooja) return false;
  const blocks = Array.isArray(pooja.blocks) ? pooja.blocks : [];
  if (
    blocks.some(
      (b) => b?.blockType === 'paragraph' && getLocalized(b?.content, lang)?.trim()
    )
  ) {
    return true;
  }
  return Boolean(String(getLocalized(pooja.description, lang) || '').trim());
}

/**
 * Merge two pooja arrays without duplicate ids (LIVE first, then UPCOMING).
 */
export function mergePoojaListsById(live, upcoming) {
  const seen = new Set();
  const out = [];
  const push = (p) => {
    if (!p) return;
    const key = p.id != null ? String(p.id) : p.slug != null ? String(p.slug) : null;
    if (key) {
      if (seen.has(key)) return;
      seen.add(key);
    }
    out.push(p);
  };
  for (const p of live || []) push(p);
  for (const p of upcoming || []) push(p);
  return out;
}

const POOJA_DETAIL_FALLBACK_SCHEDULE_TYPES = ['LIVE', 'UPCOMING', 'PAST'];

/** Schedule-aware pooja lists for detail/book fallback when `/api/poojas/details` misses. */
export function getPoojaDetailFallbackLists(lang, scheduleType, hasExplicitScheduleSegment, forceRefresh = false) {
  const st = normalizePoojaScheduleType(scheduleType, 'LIVE');
  const scheduleTypes = hasExplicitScheduleSegment ? [st] : POOJA_DETAIL_FALLBACK_SCHEDULE_TYPES;
  return Promise.all([
    ...scheduleTypes.map((type) => getPoojas(forceRefresh, lang, type).catch(() => [])),
    getPoojas(forceRefresh, lang).catch(() => []),
  ]).then((lists) => {
    const seen = new Set();
    const out = [];
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      for (const pooja of list) {
        if (!pooja) continue;
        const key = pooja.id != null ? String(pooja.id) : pooja.slug != null ? String(pooja.slug) : null;
        if (key && seen.has(key)) continue;
        if (key) seen.add(key);
        out.push(pooja);
      }
    }
    return out;
  });
}

/**
 * Home page: loads LIVE + UPCOMING from `GET /api/poojas/website?lang=...&type=LIVE` and
 * `GET /api/poojas/website?lang=...&type=UPCOMING` (not `/api/poojas/users`).
 * Order: LIVE rows first, then UPCOMING; deduped by id.
 */
export function getPoojasForHome(forceRefresh = false, lang) {
  return Promise.all([
    getPoojas(forceRefresh, lang, 'LIVE').catch(() => []),
    getPoojas(forceRefresh, lang, 'UPCOMING').catch(() => []),
  ]).then(([live, upcoming]) => mergePoojaListsById(live, upcoming));
}

/** Today's date as YYYY-MM-DD (local) for comparison */
export function getTodayString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Tomorrow as YYYY-MM-DD (local) — earliest bookable pooja day. */
export function getTomorrowString() {
  return addDaysToYmd(getTodayString(), 1);
}

export function addDaysToYmd(ymd, days) {
  const d = parseLocalDateTime(ymd, '12:00:00');
  if (!d || !Number.isFinite(days)) return '';
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getPoojaSessionDateMinYmd() {
  return getTomorrowString();
}

export function getAllowedPoojaSessionDateYmds(pooja, fee, { limit = 120 } = {}) {
  const min = getPoojaSessionDateMinYmd();
  const max = getPoojaSessionDateMaxYmd(pooja, fee);
  const out = [];
  let cur = min;
  for (let i = 0; i < limit; i += 1) {
    if (max && cur > max) break;
    if (isPoojaSessionDateAllowed(pooja, cur, { fee, minYmd: min })) {
      out.push(cur);
    }
    const next = addDaysToYmd(cur, 1);
    if (!next || next <= cur) break;
    cur = next;
  }
  return out;
}

export function getDefaultPoojaSessionDateYmd(pooja, fee) {
  const allowed = getAllowedPoojaSessionDateYmds(pooja, fee);
  return allowed[0] || '';
}

export function isPersonalizedPooja(pooja) {
  const t = String(pooja?.poojaType ?? pooja?.pooja_type ?? '').trim().toUpperCase();
  return t === 'PERSONALIZED';
}

/** Blocked calendar days from API (`blockedDates` / ISO datetimes). */
export function getPoojaBlockedDateYmdSet(pooja, fee) {
  const sources = [
    pooja?.blockedDates,
    pooja?.blocked_dates,
    fee?.blockedDates,
    fee?.blocked_dates,
  ];
  const set = new Set();
  const addFromArray = (raw) => {
    if (!Array.isArray(raw)) return;
    for (const item of raw) {
      const s = String(item ?? '').trim();
      if (!s) continue;
      const datePart = s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
      const ymd = normalizeDateToYYYYMMDD(datePart);
      if (ymd) set.add(ymd);
    }
  };
  for (const raw of sources) addFromArray(raw);
  for (const pf of pooja?.participationFees || pooja?.participation_fees || []) {
    addFromArray(pf?.blockedDates);
    addFromArray(pf?.blocked_dates);
  }
  return set;
}

export function getPoojaSessionDateMaxYmd(pooja, fee) {
  const candidates = [
    fee?.availableTillDate,
    fee?.available_till_date,
    pooja?.lastBookingDate,
    pooja?.last_booking_date,
  ];
  for (const raw of candidates) {
    const ymd = normalizeDateToYYYYMMDD(String(raw || '').trim());
    if (ymd) return ymd;
  }
  return '';
}

/** Session / pooja date picker: reject past/today, after booking window, and blocked days. */
export function isPoojaSessionDateAllowed(pooja, ymd, { fee, minYmd } = {}) {
  const normalized = normalizeDateToYYYYMMDD(String(ymd || '').trim());
  if (!normalized) return false;
  const min = minYmd || getPoojaSessionDateMinYmd();
  if (normalized < min) return false;
  const max = getPoojaSessionDateMaxYmd(pooja, fee);
  if (max && normalized > max) return false;
  if (getPoojaBlockedDateYmdSet(pooja, fee).has(normalized)) {
    return false;
  }
  return true;
}

function getNow() {
  return new Date();
}

/** Normalize time string to HH:MM:SS for local parsing (handles "20:50", "20:50:20", "8:50 PM") */
function normalizeTimeToHHMMSS(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '23:59:59';
  const t = timeStr.trim();
  if (!t) return '23:59:59';
  const m12 = t.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10) || 0;
    const min = parseInt(m12[2], 10) || 0;
    if ((m12[3] || '').toLowerCase() === 'pm' && h < 12) h += 12;
    if ((m12[3] || '').toLowerCase() === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`;
  }
  const parts = t.split(':').map((p) => parseInt(String(p).trim(), 10) || 0);
  const h = Math.min(23, Math.max(0, parts[0] ?? 0));
  const min = Math.min(59, Math.max(0, parts[1] ?? 0));
  const sec = Math.min(59, Math.max(0, parts[2] ?? 0));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** Normalize date to YYYY-MM-DD (handles YYYY-MM-DD, DD-MM-YYYY) */
function normalizeDateToYYYYMMDD(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const s = dateStr.trim();
  if (!s) return '';
  const parts = s.split(/[-/]/).map((p) => String(p).trim());
  if (parts.length >= 3) {
    const a = parseInt(parts[0], 10);
    const b = parseInt(parts[1], 10);
    const c = parseInt(parts[2], 10);
    if (a > 31) return `${a}-${String(b).padStart(2, '0')}-${String(c).padStart(2, '0')}`;
    if (c > 31) return `${c}-${String(b).padStart(2, '0')}-${String(a).padStart(2, '0')}`;
  }
  return s;
}

/** Parse date+time as local time (user's timezone) */
export function parseLocalDateTime(dateStr, timeStr = '23:59:59') {
  if (!dateStr) return null;
  const dateNorm = normalizeDateToYYYYMMDD(dateStr);
  const time = normalizeTimeToHHMMSS(timeStr);
  const d = new Date(`${dateNorm}T${time}`);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

export function getPoojaBookingDeadline(pooja) {
  const deadlineDate = pooja?.lastBookingDate || pooja?.poojaDate || '';
  const deadlineTime = pooja?.lastBookingTime || '23:59:59';
  const deadline = parseLocalDateTime(deadlineDate, deadlineTime);
  if (!deadline || Number.isNaN(deadline.getTime())) return null;
  return deadline;
}

/**
 * True if Last Booking Date is set and has passed.
 * When lastBookingDate is empty → no close date → never passed (per spec).
 */
export function hasBookingDeadlinePassed(pooja) {
  const lastBookingDate = (pooja?.lastBookingDate ?? pooja?.last_booking_date ?? '').toString().trim();
  if (!lastBookingDate) return false;
  const deadlineTime = pooja?.lastBookingTime ?? pooja?.last_booking_time ?? '23:59:59';
  const deadline = parseLocalDateTime(normalizeDateToYYYYMMDD(lastBookingDate), deadlineTime);
  if (!deadline) return false;
  return deadline.getTime() < getNow().getTime();
}

/**
 * Live Pooja:
 * - status is ACTIVE
 * - last booking date + time > current date + time (deadline not passed)
 * - campaign start date is today or earlier (pooja is currently open)
 */
export function isLivePooja(pooja) {
  const today = getTodayString();
  const campaignStartDate = pooja?.poojaCampaignStartDate || pooja?.poojaDate || '';
  return isPoojaActiveStatus(pooja) && campaignStartDate <= today && !hasBookingDeadlinePassed(pooja);
}

/** Active pooja whose booking deadline has not passed (live or upcoming). */
export function isPoojaBookable(pooja) {
  return isPoojaActiveStatus(pooja) && !hasBookingDeadlinePassed(pooja);
}

/**
 * Upcoming Pooja:
 * - status is ACTIVE
 * - campaign date > today (campaign not yet started)
 */
export function isUpcomingPooja(pooja) {
  const today = getTodayString();
  const campaignStartDate = pooja?.poojaCampaignStartDate || pooja?.poojaDate || '';
  return isPoojaActiveStatus(pooja) && campaignStartDate > today && !hasBookingDeadlinePassed(pooja);
}

/**
 * Poojas Performed (past):
 * - status is ACTIVE
 * - last booking date + time < current date + time (deadline passed)
 * Inactive poojas are excluded from all lists (Live, Upcoming, Past).
 */
export function isPastPooja(pooja) {
  return isPoojaActiveStatus(pooja) && hasBookingDeadlinePassed(pooja);
}

/** API: isMegaPooja + megaPoojaDetails.schedule (per-day time slots). */
export function isMegaPooja(pooja) {
  if (!pooja) return false;
  const flag = pooja.isMegaPooja ?? pooja.is_mega_pooja;
  if (flag === true || flag === 1 || flag === 'true' || flag === '1') return true;
  const m = pooja.megaPoojaDetails ?? pooja.mega_pooja_details;
  return Array.isArray(m?.schedule) && m.schedule.length > 0;
}

/**
 * Community / group WhatsApp invite URL from pooja API (`whatsappUrl`, etc.).
 * Returns normalized https URL string or ''.
 */
export function getPoojaCommunityWhatsAppUrl(pooja) {
  if (!pooja) return '';
  const raw = pooja.whatsappUrl ?? pooja.whatsAppUrl ?? pooja.whatsapp_url ?? '';
  if (raw == null) return '';
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return '';
  const lower = s.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return '';
  if (!/^https?:\/\//i.test(s)) return '';
  try {
    return new URL(s).href;
  } catch {
    return '';
  }
}

export function getMegaPoojaSchedule(pooja) {
  const m = pooja?.megaPoojaDetails ?? pooja?.mega_pooja_details;
  const sched = m?.schedule;
  return Array.isArray(sched) ? sched : [];
}

/** Normalize schedule row date to YYYY-MM-DD. */
export function getMegaScheduleRowDateYmd(row) {
  const raw = String(row?.date ?? '').trim();
  if (!raw) return '';
  const datePart = raw.includes('T') ? raw.split('T')[0] : raw.slice(0, 10);
  return normalizeDateToYYYYMMDD(datePart) || datePart;
}

/** Default cutoff for “today” when API omits `lastBookingTime` (matches previous hardcoded 10:00). */
const MEGA_TODAY_DEFAULT_CUTOFF_TIME = '10:00:00';

/**
 * Local Date for end of same-day mega booking window: today at `lastBookingTime` (API), local timezone.
 * Uses `pooja.lastBookingTime` / `last_booking_time` (e.g. "14:00:00"); falls back to 10:00:00 if missing.
 */
function getMegaTodayBookingCutoffInstant(pooja) {
  const raw = (pooja?.lastBookingTime || pooja?.last_booking_time || '').trim();
  const time = raw ? normalizeTimeToHHMMSS(raw) : MEGA_TODAY_DEFAULT_CUTOFF_TIME;
  const today = getTodayString();
  const instant = new Date(`${today}T${time}`);
  if (Number.isNaN(instant.getTime())) return null;
  return instant;
}

/**
 * Whether a schedule YYYY-MM-DD is still bookable: future days yes; past days no;
 * for **today**, only while local time is **before** `pooja.lastBookingTime` (same day).
 */
export function isMegaScheduleDateBookable(ymd, pooja) {
  const d = getMegaScheduleRowDateYmd({ date: ymd }) || String(ymd || '').trim();
  if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  const today = getTodayString();
  if (d < today) return false;
  if (d > today) return true;
  const cutoff = getMegaTodayBookingCutoffInstant(pooja);
  if (!cutoff) return false;
  return getNow().getTime() < cutoff.getTime();
}

/** Schedule rows bookable from “today” onward — today’s row drops after `lastBookingTime` local. */
export function getMegaPoojaScheduleFromToday(pooja) {
  const schedule = getMegaPoojaSchedule(pooja);
  return schedule.filter((row) => isMegaScheduleDateBookable(getMegaScheduleRowDateYmd(row), pooja));
}

/**
 * YYYY-MM-DD values from mega schedule that may still be selected in booking
 * (API schedule only; passed / after lastBookingTime on today are excluded).
 */
export function getMegaBookableDateYmds(pooja, fee, lang) {
  if (!isMegaPooja(pooja)) return [];
  const rows = fee
    ? getMegaScheduleRowsForParticipationFee(pooja, fee, lang)
    : getMegaPoojaScheduleFromToday(pooja);
  const out = [];
  const seen = new Set();
  for (const row of rows) {
    const ymd = getMegaScheduleRowDateYmd(row);
    if (!ymd || seen.has(ymd)) continue;
    if (!isMegaScheduleDateBookable(ymd, pooja)) continue;
    seen.add(ymd);
    out.push(ymd);
  }
  return out;
}

/**
 * Multi-day fee label (e.g. "3 Days") matching all remaining schedule days → full span; no single session date pick.
 */
export function isParticipationFeeFullScheduleSpan(fee, pooja, lang) {
  if (!fee || !isMegaPooja(pooja)) return false;
  const future = getMegaPoojaScheduleFromToday(pooja);
  const n = future.length;
  if (n < 2) return false;
  const label = getLocalized(fee.label, lang) || '';
  const daysMatch = label.match(/(\d+)\s*[-–]?\s*days?/i);
  if (daysMatch && parseInt(daysMatch[1], 10) === n) return true;
  if (/\b(full|complete|entire)\b/i.test(label) && /\b(schedule|program|programme|pooja|path)\b/i.test(label)) return true;
  const allDays = label.match(/all\s+(\d+)\s+days?/i);
  if (allDays && parseInt(allDays[1], 10) === n) return true;
  return false;
}

/** Day count from fee fields or a label such as "5-Day" / "5 days". */
function participationFeeScheduleDayCount(fee, lang) {
  const rawDays =
    fee?.durationDays ??
    fee?.duration_days ??
    fee?.dayCount ??
    fee?.day_count ??
    fee?.megaScheduleDays ??
    fee?.mega_schedule_days;
  if (rawDays != null && rawDays !== '') {
    const parsed = parseInt(String(rawDays), 10);
    if (!Number.isNaN(parsed) && parsed >= 1) return parsed;
  }
  const label = getLocalized(fee?.label, lang) || '';
  const daysMatch = label.match(/(\d+)\s*[-–]?\s*days?/i);
  if (daysMatch) {
    const n = parseInt(daysMatch[1], 10);
    if (!Number.isNaN(n) && n >= 1) return n;
  }
  return null;
}

/**
 * True when the devotee picks one session from the full schedule ("1 Day", or a fee with no day count).
 * Multi-day packages such as "5-Day" are not a single-date choice and do not drop later schedule days.
 */
export function isParticipationFeeChooseOneSession(fee, pooja, lang) {
  if (!fee || !isMegaPooja(pooja)) return false;
  if (isParticipationFeeFullScheduleSpan(fee, pooja, lang)) return false;
  const n = participationFeeScheduleDayCount(fee, lang);
  if (n != null && n > 1) return false;
  return true;
}

/**
 * Bookable mega schedule rows for a package.
 * "1 day" keeps every bookable date so the devotee can choose a session.
 * Multi-day packages (for example "5-Day") also keep every bookable date — they are not cut to the first N days.
 */
export function getMegaScheduleRowsForParticipationFee(pooja, fee, lang) {
  const megaSchedule = getMegaPoojaScheduleFromToday(pooja);
  if (!fee || !isMegaPooja(pooja) || megaSchedule.length <= 1) return megaSchedule;
  return megaSchedule;
}

/** "06:00 AM" / "18:30" → HH:MM (24h) for booking_details.time */
export function megaTimeSlotToHHMM(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const t = timeStr.trim();
  if (!t) return '';
  const m12 = t.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10) || 0;
    const min = parseInt(m12[2], 10) || 0;
    if ((m12[3] || '').toLowerCase() === 'pm' && h < 12) h += 12;
    if ((m12[3] || '').toLowerCase() === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  const parts = t.split(':').map((p) => String(p).trim());
  const h = parseInt(parts[0], 10);
  const min = parseInt(parts[1], 10);
  if (Number.isNaN(h)) return '';
  const hh = Math.min(23, Math.max(0, h));
  const mm = Number.isNaN(min) ? 0 : Math.min(59, Math.max(0, min));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function getMegaTimeSlotsForDate(pooja, dateStr) {
  const d = getMegaScheduleRowDateYmd({ date: dateStr }) || String(dateStr || '').trim();
  const row = getMegaPoojaSchedule(pooja).find((s) => getMegaScheduleRowDateYmd(s) === d);
  return Array.isArray(row?.timeSlots) ? row.timeSlots : [];
}

export function isMegaBookingSlotValid(pooja, dateStr, timeHHMM) {
  if (!isMegaPooja(pooja)) return true;
  const schedule = getMegaPoojaSchedule(pooja);
  if (schedule.length === 0) return true;
  const d = getMegaScheduleRowDateYmd({ date: dateStr }) || String(dateStr || '').trim();
  if (!d) return false;
  if (!isMegaScheduleDateBookable(d, pooja)) return false;
  if (!schedule.some((row) => getMegaScheduleRowDateYmd(row) === d)) return false;
  const slots = getMegaTimeSlotsForDate(pooja, d);
  const allowed = slots.map((s) => megaTimeSlotToHHMM(s)).filter(Boolean);
  if (allowed.length === 0) {
    // timeSlots null/empty → date-only booking; date must be on schedule and still bookable
    return true;
  }
  return Boolean(timeHHMM && allowed.includes(timeHHMM));
}

/** YYYY-MM-DD → short localized label for selects / schedule list */
export function formatYmdDateDisplay(ymd, lang) {
  if (!ymd || typeof ymd !== 'string') return '';
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d || Number.isNaN(y)) return ymd;
  const dt = new Date(y, m - 1, d);
  const locale = getLocaleForLang(lang);
  try {
    return dt.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return ymd;
  }
}

/** YYYY-MM-DD → compact "5 Apr" (day + short month; no weekday/year) — mega schedule headlines */
export function formatYmdDayMonthShort(ymd, lang) {
  if (!ymd || typeof ymd !== 'string') return '';
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d || Number.isNaN(y)) return ymd;
  const dt = new Date(y, m - 1, d);
  const locale = getLocaleForLang(lang);
  try {
    return dt.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  } catch {
    return ymd;
  }
}

/** Mega schedule chip: `past` | `today` (filled) vs `future` (outline / not filled). */
export function getMegaScheduleDateChipVariant(ymd) {
  const today = getTodayString();
  const d = String(ymd || '').trim();
  if (!d) return 'future';
  if (d < today) return 'past';
  if (d === today) return 'today';
  return 'future';
}

export function getPoojaTitle(pooja, lang) {
  const t = pooja?.title;
  if (typeof t === 'string') return t;
  const fromObj = getLocalized(t, lang);
  return fromObj || 'Pooja';
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Normalize slug or title for API: "Daivbharathi Astro Consultation" -> "daivbharathi-astro-consultation". */
export function slugifyForApi(slugOrTitle) {
  if (!slugOrTitle || typeof slugOrTitle !== 'string') return '';
  const s = String(slugOrTitle).trim();
  if (!s) return '';
  if (UUID_REGEX.test(s)) return s.toLowerCase();
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'pooja';
}

/** Derive SEO slug from pooja title: lowercase, hyphens for spaces, collapse multiple hyphens */
export function getPoojaSlug(pooja, lang) {
  const apiSlug = pooja?.slug;
  if (typeof apiSlug === 'string' && apiSlug.trim()) {
    return slugifyForApi(apiSlug.trim());
  }
  const title = getPoojaTitle(pooja, lang);
  if (!title || typeof title !== 'string') return 'pooja';
  const slug = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || 'pooja';
}

/** Default About section heading when API `aboutTitle` is empty. */
export function getPoojaAboutTitleKey() {
  return 'poojaDetail.aboutRitual';
}

/** API `aboutTitle` (localized) when set; otherwise empty — pair with i18n fallback in UI. */
export function getPoojaAboutTitle(pooja, lang) {
  const title = getLocalized(pooja?.aboutTitle, lang);
  return title && String(title).trim() ? String(title).trim() : '';
}

/** Resolved About heading: API title first, else localized “About This Ritual”. */
export function getPoojaAboutSectionTitle(pooja, lang, t) {
  return getPoojaAboutTitle(pooja, lang) || t(getPoojaAboutTitleKey(), lang);
}

/** Get slug with optional id suffix for collision handling (e.g. maha-sudarshana-homa-05e820f4) */
function getPoojaSlugWithId(pooja, lang) {
  const base = getPoojaSlug(pooja, lang);
  const idPrefix = pooja?.id ? String(pooja.id).slice(0, 8) : '';
  return idPrefix ? `${base}-${idPrefix}` : base;
}

const SLUG_LANGS = LANG_CODES;

/** Check if pooja's slug (in any language) matches the param. Used when URL slug is fixed but lang may change. */
function poojaSlugMatchesParam(pooja, param) {
  const raw = pooja?.slug;
  if (typeof raw === 'string' && raw.trim()) {
    const normalized = slugifyForApi(raw.trim());
    if (normalized === param || raw.trim().toLowerCase() === String(param).trim().toLowerCase()) return true;
  }
  for (const l of SLUG_LANGS) {
    if (getPoojaSlug(pooja, l) === param) return true;
    if (getPoojaSlugWithId(pooja, l) === param) return true;
  }
  return false;
}

/** Find pooja by slug or UUID. Matches slug across all languages so language switch does not break lookup. */
export function findPoojaBySlugOrId(poojas, slugOrId, lang) {
  if (!Array.isArray(poojas) || !slugOrId) return null;
  const param = String(slugOrId).trim();

  if (UUID_REGEX.test(param)) {
    return poojas.find((p) => p.id === param) || null;
  }

  const found = poojas.find((p) => poojaSlugMatchesParam(p, param));
  if (found) return found;
  const normalized = slugifyForApi(param);
  if (normalized !== param) {
    return poojas.find((p) => poojaSlugMatchesParam(p, normalized)) || null;
  }
  return null;
}

/** Get the canonical slug for a pooja when building URLs. Uses base slug if unique; else slug-with-id for collisions. */
export function getPoojaSlugForUrl(pooja, allPoojas, lang) {
  if (!pooja) return 'pooja';
  const base = getPoojaSlug(pooja, lang);
  const sameSlug = (allPoojas || []).filter((p) => getPoojaSlug(p, lang) === base);
  if (sameSlug.length <= 1) return base;
  return getPoojaSlugWithId(pooja, lang);
}

/** Check if slugOrId (from URL) matches the given pooja (by id or slug in any language). */
export function slugOrIdMatchesPooja(slugOrId, pooja, lang) {
  if (!slugOrId || !pooja) return false;
  const param = String(slugOrId).trim();
  if (pooja.id === param) return true;
  if (poojaSlugMatchesParam(pooja, param)) return true;
  const normalized = slugifyForApi(param);
  return normalized !== param && poojaSlugMatchesParam(pooja, normalized);
}

/** Prefer cover image for listing; use for home strip when available */
export function getPoojaCoverImage(pooja) {
  return pooja?.coverImageUrl || '';
}

/** Home hero carousel — LIVE poojas with CMS cover (`hasCoverImageUrl: true` + `coverImageUrl`). */
export function poojaHasHeroCoverImage(pooja) {
  if (pooja?.hasCoverImageUrl !== true) return false;
  return Boolean(String(getPoojaCoverImage(pooja) || '').trim());
}

/** Absolute URL for home hero cover banner — only when `poojaHasHeroCoverImage(pooja)`. */
export function getPoojaHeroCoverImageUrl(pooja) {
  if (!poojaHasHeroCoverImage(pooja)) return '';
  const raw = String(getPoojaCoverImage(pooja) || '').trim();
  if (!raw) return '';
  return resolveImageUrl(raw) || raw;
}

/** Mobile cover image – use for mobile view when coverImageApp is set (file/URL) */
export function getPoojaCoverImageApp(pooja, lang) {
  const v = pooja?.coverImageApp;
  if (!v) return '';
  if (typeof v === 'string') return v;
  return getLocalized(v, lang) || v?.en || v?.url || v?.fileUrl || '';
}

/**
 * Pooja card / list / hero image — `poojaImages` only (never `coverImageUrl`).
 */
export function getPoojaImage(pooja, lang) {
  const imgs = pooja?.poojaImages;
  if (typeof imgs === 'string' && imgs.trim()) return imgs.trim();
  if (lang && imgs?.[lang]) return imgs[lang];
  const fromImgs = imgs?.en || imgs?.hi || imgs?.kn || imgs?.te;
  if (fromImgs) return fromImgs;

  return pooja?.thumbnailUrl || pooja?.imageUrl || pooja?.image || '';
}

/**
 * Extra hero side-slide images from `poojaImagesInfo` (detail carousel).
 * When present, these replace benefits/included/acharya text sheets in the hero.
 */
export function getPoojaImagesInfo(pooja, lang = 'en') {
  const raw = pooja?.poojaImagesInfo ?? pooja?.pooja_images_info;
  const list = Array.isArray(raw)
    ? raw
    : (raw && typeof raw === 'object' ? pickLocalizedArrayFromMap(raw, lang) : null);
  if (!Array.isArray(list) || list.length === 0) return [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    let url = '';
    if (typeof item === 'string') url = item.trim();
    else if (item && typeof item === 'object') {
      url = String(item.url || item.imageUrl || item.src || item.fileUrl || '').trim();
    }
    if (!url) continue;
    const resolved = resolveImageUrl(url) || url;
    if (!resolved || seen.has(resolved)) continue;
    seen.add(resolved);
    out.push(resolved);
  }
  return out;
}

/**
 * Hero carousel slides that Pooja Detail actually renders: the cover, then info photos.
 * Text sheets are not slides — counting them hid the photo on an empty frame.
 */
export function getPoojaDetailHeroSlideIds(pooja, lang = 'en') {
  const ids = ['image'];
  const infoImages = getPoojaImagesInfo(pooja, lang);
  infoImages.forEach((_, i) => ids.push(`info-image-${i}`));
  return ids;
}

/** Get pooja image from booking API response (`poojaImage`, `poojaImage_en`, etc.) */
export function getBookingPoojaImage(booking, lang = 'en') {
  if (!booking) return '';

  const pickUrl = (raw) => {
    if (typeof raw === 'string' && raw.trim()) return resolveImageUrl(raw.trim());
    return '';
  };

  const pickFromRecord = (record) => {
    if (!record || typeof record !== 'object') return '';
    const key = `poojaImage_${lang}`;
    const snakeKey = `pooja_image_${lang}`;
    return (
      pickUrl(record[key]) ||
      pickUrl(record[snakeKey]) ||
      pickUrl(record.poojaImage) ||
      pickUrl(record.pooja_image) ||
      pickUrl(record.poojaImage_en) ||
      pickUrl(record.pooja_image_en) ||
      pickUrl(record.poojaImage_hi) ||
      pickUrl(record.pooja_image_hi) ||
      pickUrl(record.poojaImage_kn) ||
      pickUrl(record.pooja_image_kn) ||
      pickUrl(record.poojaImage_te) ||
      pickUrl(record.pooja_image_te) ||
      ''
    );
  };

  return (
    pickFromRecord(booking) ||
    pickFromRecord(booking.booking_details ?? booking.bookingDetails) ||
    pickFromRecord(booking.poojaDetails) ||
    ''
  );
}

function pickLocalizedFromMap(map, lang) {
  if (!map || typeof map !== 'object') return '';
  const preferred = localizedLangKeys(lang);
  const order = [
    ...preferred,
    ...LOCALIZED_FALLBACK_ORDER.filter((c) => !preferred.includes(c)),
  ];
  for (const k of order) {
    const v = map[k];
    if (typeof v === 'string' && v) return v;
  }
  for (const k of order) {
    const v = map[k];
    if (v && typeof v === 'object') {
      const inner = pickLocalizedFromMap(v, lang);
      if (inner) return inner;
    }
  }
  return '';
}

function pickLocalizedArrayFromMap(map, lang) {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
  // Exact language only — never show another language's bullets (e.g. EN UI + KN text).
  for (const key of localizedLangKeys(lang)) {
    const preferred = map[key];
    if (Array.isArray(preferred) && preferred.length > 0) return preferred;
  }
  // Non-English UI may fall back to English only — not to other regional languages.
  if (normalizeUiLang(lang) !== 'en') {
    const en = map.en;
    if (Array.isArray(en) && en.length > 0) return en;
  }
  return null;
}

/** HTML bullet lists from API — array, localized `{ en: [...] }`, or mixed items. */
export function getLocalizedHtmlList(value, lang = 'en') {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item : getLocalized(item, lang)))
      .map((s) => String(s || '').trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    const s = value.trim();
    return s ? [s] : [];
  }
  const localized = pickLocalizedArrayFromMap(value, lang);
  if (localized) {
    return localized
      .map((item) => (typeof item === 'string' ? item : getLocalized(item, lang)))
      .map((s) => String(s || '').trim())
      .filter(Boolean);
  }
  return [];
}

export function getLocalized(obj, lang = 'en') {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return pickLocalizedFromMap(obj, lang);
}

function stripHtml(value) {
  if (!value || typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Render API rich text: pass through HTML or convert plain newlines to `<br />`. */
export function formatRichTextHtml(content) {
  const s = String(content || '').trim();
  if (!s) return '';
  return s.includes('<') ? s : s.replace(/\n/g, '<br />');
}

/** Format pooja date for card badge e.g. "MARCH 16" – uses lastBookingDate when available, else poojaDate */
export function formatPoojaDateBadge(pooja) {
  const raw = pooja?.lastBookingDate || pooja?.poojaDate || '';
  if (!raw) return '';
  const normalized = normalizeDateToYYYYMMDD(raw);
  if (!normalized) return '';
  const parts = normalized.split('-');
  const m = parts[1] || '';
  const d = parts[2] || '';
  const months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
  const month = months[parseInt(m, 10) - 1] || m;
  const day = d ? parseInt(d, 10) : '';
  return day ? `${month} ${day}` : raw;
}

/** Format a YYYY-MM-DD date string for display e.g. "9th March 2026" */
export function formatDateDisplay(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const month = months[(m || 1) - 1] || '';
  const day = d || 0;
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
  return `${day}${suffix} ${month} ${y || ''}`.trim();
}

/** Catalog card pill — no st/nd/rd/th e.g. "19 Mar 2026" */
export function formatDateDisplayCard(dateStr) {
  if (!dateStr) return '';
  const normalized = normalizeDateToYYYYMMDD(String(dateStr).trim());
  if (!normalized) return '';
  const [y, m, d] = normalized.split('-').map(Number);
  const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthsShort[(m || 1) - 1] || '';
  const day = d || 0;
  if (!day || !month) return '';
  return y ? `${day} ${month} ${y}` : `${day} ${month}`;
}

/** Format poojaDate for detail page */
export function formatPoojaDateDisplay(pooja) {
  return formatDateDisplay(pooja?.poojaDate || '');
}

/** YYYY-MM-DD → "3-July-2026 Friday" (detail hero — ritual / auspicious date). */
export function formatPoojaRitualDateHero(ymd, lang) {
  if (!ymd) return '';
  const normalized = normalizeDateToYYYYMMDD(String(ymd).trim());
  if (!normalized) return '';
  const [y, m, d] = normalized.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d || Number.isNaN(y)) return '';
  const dt = new Date(y, m - 1, d);
  try {
    return dt.toLocaleDateString(getLocaleForLang(lang), {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return '';
  }
}

/** Ritual date label for pooja detail hero. */
export function formatPoojaRitualDateDisplay(pooja, lang) {
  return formatPoojaRitualDateHero(getPoojaRitualDateYmd(pooja), lang);
}

/** Next date on pooja listing cards (compact, no ordinals). */
export function formatPoojaDateDisplayCard(pooja) {
  if (!pooja) return '';
  return (
    formatDateDisplayCard(pooja.poojaDate || pooja.pooja_date || '') ||
    formatDateDisplayCard(pooja.lastBookingDate || pooja.last_booking_date || '')
  );
}

/** YYYY-MM-DD ritual date shown on catalog cards. */
export function getPoojaCatalogDateYmd(pooja) {
  if (!pooja) return '';
  let dateYmd = normalizeDateToYYYYMMDD(String(pooja.poojaDate || pooja.pooja_date || '').trim());
  if (!dateYmd && isMegaPooja(pooja)) {
    const schedule = getMegaPoojaScheduleFromToday(pooja);
    const first = schedule.find((row) => row?.date);
    if (first?.date) dateYmd = normalizeDateToYYYYMMDD(String(first.date).trim());
  }
  if (!dateYmd) {
    dateYmd = normalizeDateToYYYYMMDD(String(pooja.lastBookingDate || pooja.last_booking_date || '').trim());
  }
  return dateYmd || '';
}

/**
 * Sort key for pooja catalog grids — ritual date + time (soonest first).
 * Uses the same ritual date shown on cards (not campaign start / creation date).
 */
export function getPoojaCatalogSortTime(pooja) {
  if (!pooja) return Infinity;

  const dateYmd = getPoojaCatalogDateYmd(pooja);
  if (!dateYmd) {
    const deadline = getPoojaBookingDeadline(pooja);
    return deadline ? deadline.getTime() : Infinity;
  }

  const timeStr =
    pooja.poojaTime ??
    pooja.pooja_time ??
    pooja.lastBookingTime ??
    pooja.last_booking_time ??
    '00:00:00';
  const dt = parseLocalDateTime(dateYmd, timeStr);
  return dt ? dt.getTime() : Infinity;
}

/** YYYY-MM-DD ritual date for booking initiate (`booking_details.date`). */
export function getPoojaRitualDateYmd(pooja) {
  if (!pooja) return '';
  const candidates = [
    pooja.poojaDate,
    pooja.pooja_date,
    pooja.ritualDate,
    pooja.ritual_date,
  ];
  for (const raw of candidates) {
    const normalized = normalizeDateToYYYYMMDD(String(raw || '').trim());
    if (normalized) return normalized;
  }
  if (isMegaPooja(pooja)) {
    const schedule = getMegaPoojaScheduleFromToday(pooja);
    const first = schedule.find((row) => row?.date);
    if (first?.date) return normalizeDateToYYYYMMDD(String(first.date));
  }
  return '';
}

/** HH:mm ritual time when the pooja defines one (optional on initiate). */
export function getPoojaRitualTimeHm(pooja) {
  if (!pooja) return '';
  const raw =
    pooja.poojaTime ??
    pooja.pooja_time ??
    pooja.ritualTime ??
    pooja.ritual_time ??
    '';
  const s = String(raw || '').trim();
  if (!s || s === '23:59:59') return '';
  const parts = s.split(':').map((p) => parseInt(String(p).trim(), 10) || 0);
  const h = Math.min(23, Math.max(0, parts[0] ?? 0));
  const min = Math.min(59, Math.max(0, parts[1] ?? 0));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Local Date for ritual (pooja date + optional time) — used for card countdowns. */
export function getPoojaRitualDateTime(pooja) {
  const dateYmd = getPoojaRitualDateYmd(pooja);
  if (!dateYmd) return null;
  const timeHm = getPoojaRitualTimeHm(pooja);
  const timeStr = timeHm ? `${timeHm}:00` : '00:00:00';
  return parseLocalDateTime(dateYmd, timeStr);
}

/** Compact countdown e.g. "5d 12h 30m 45s" — null when target missing or in the past. */
export function formatPoojaCountdownRemaining(targetDate) {
  const parts = getPoojaCountdownParts(targetDate);
  if (!parts) return null;
  const { days: d, hours: h, minutes: m, seconds: s } = parts;
  return `${d}d ${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

/**
 * Cover-banner countdown. Within 24h: "30hr : 30Min : 50s".
 * Beyond 24h: "1 day to go" / "2 days to go" (no live hr:min:sec ticking).
 */
export function formatPoojaCountdownHoursMinSec(targetDate) {
  const parts = getPoojaCountdownParts(targetDate);
  if (!parts) return null;
  if (parts.days >= 1) {
    return `${parts.days} day${parts.days > 1 ? 's' : ''} to go`;
  }
  const min = String(parts.minutes).padStart(2, '0');
  const sec = String(parts.seconds).padStart(2, '0');
  return `${parts.hours}hr : ${min}Min : ${sec}s`;
}

/** Countdown parts for boxed UI — null when target missing or in the past. */
export function getPoojaCountdownParts(targetDate) {
  if (!targetDate || !(targetDate instanceof Date) || Number.isNaN(targetDate.getTime())) return null;
  const diff = targetDate.getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

/** True when a card should show the live ritual-date countdown (before it passes). */
export function hasUpcomingPoojaRitualCountdown(pooja) {
  if (!pooja || isPastPooja(pooja)) return false;
  const target = getPoojaRitualDateTime(pooja);
  if (!target) return false;
  return target.getTime() > Date.now();
}

/** True when the cover/hero should show time left until booking closes. */
export function hasUpcomingPoojaBookingCountdown(pooja) {
  if (!pooja || hasBookingDeadlinePassed(pooja)) return false;
  const target = getPoojaBookingDeadline(pooja);
  if (!target) return false;
  return target.getTime() > Date.now();
}

/** Normalize any date string to YYYY-MM-DD for booking initiate. */
export function normalizeBookingDateYmd(dateStr) {
  return normalizeDateToYYYYMMDD(String(dateStr || '').trim());
}

/** Format lastBookingDate for detail page */
export function formatLastBookingDateDisplay(pooja) {
  return formatDateDisplay(pooja?.lastBookingDate || '');
}

/** Format HH:MM:SS or HH:MM to 12h display e.g. "10:30 AM" */
export function formatTimeForDisplay(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const parts = timeStr.trim().split(':').map((p) => parseInt(p, 10) || 0);
  const hours24 = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
}

/** Format lastBookingDate + lastBookingTime for detail page (booking deadline) */
export function formatPoojaBookingDeadlineDisplay(pooja) {
  const dateStr = pooja?.lastBookingDate || pooja?.poojaDate || '';
  const timeStr = (pooja?.lastBookingTime || '').trim();
  const datePart = formatDateDisplay(dateStr);
  if (!datePart) return '';
  if (!timeStr || timeStr === '23:59:59') return datePart;
  const timeDisplay = formatTimeForDisplay(timeStr);
  return timeDisplay ? `${datePart}, ${timeDisplay}` : datePart;
}

/** Venue/location for card subtitle (API uses address) */
export function getPoojaVenue(pooja, lang) {
  const v = pooja?.venue || pooja?.location || pooja?.address;
  return getLocalized(v, lang) || '';
}

/** USP label on card image — only when API sends a non-empty uspValue */
export function getPoojaUspValue(pooja, lang) {
  if (!pooja) return '';
  const candidates = [
    pooja.uspValue,
    pooja.usp_value,
    pooja.poojaDetails?.uspValue,
    pooja.poojaDetails?.usp_value,
  ];
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const text = getLocalized(raw, lang);
    if (typeof text === 'string' && text.trim()) return text.trim();
  }
  return '';
}

/** Normalize API usp type string (e.g. ACHARYA, kshetra) to icon kind. */
export function getPoojaUspIconKindFromType(uspType, pooja = null) {
  const usp = String(uspType ?? pooja?.usp ?? pooja?.usp_type ?? '').trim().toLowerCase();
  if (/acharya|pandit/.test(usp)) return 'acharya';
  if (/thithi|tithi/.test(usp)) return 'tithi';
  if (/grace|daysofgrace/.test(usp)) return 'daysOfGrace';
  if (/kshetra/.test(usp) || pooja?.kshetraId) return 'kshetra';
  if (/address|venue|location/.test(usp)) return 'kshetra';
  if (/date|booking|poojadate/.test(usp)) return 'tithi';
  return 'kshetra';
}

function getPoojaUspsArray(pooja) {
  const candidates = [pooja?.usps, pooja?.poojaDetails?.usps];
  for (const arr of candidates) {
    if (Array.isArray(arr) && arr.length > 0) return arr;
  }
  return null;
}

function resolveUspValuesList(values, lang) {
  if (!Array.isArray(values) || values.length === 0) return [];
  return values
    .map((v) => {
      if (v == null || v === '') return '';
      if (typeof v === 'object' && !Array.isArray(v)) {
        const ymd = String(v.date ?? v.ymd ?? v.value ?? '').trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
        const localizedObj = getLocalized(v, lang);
        if (typeof localizedObj === 'string' && localizedObj.trim()) return localizedObj.trim();
        return '';
      }
      const localized = getLocalized(v, lang);
      if (typeof localized === 'string' && localized.trim()) return localized.trim();
      if (typeof v === 'string' || typeof v === 'number') return String(v).trim();
      return '';
    })
    .filter(Boolean);
}

function resolveUspValuesText(values, lang) {
  return resolveUspValuesList(values, lang).join(', ');
}

function isDateLikeUspType(uspType) {
  const usp = String(uspType || '').trim().toLowerCase();
  /* Pure tithi/thithi names are not calendar date lists */
  if (/^(thithi|tithi)$/.test(usp)) return false;
  return /date|booking|poojadate|schedule|ritual/.test(usp);
}

/** Split comma/dot-joined date blobs into individual tokens. */
function splitUspDateTokens(text) {
  const raw = String(text || '').trim();
  if (!raw) return [];
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return [raw];
  return raw
    .split(/\s*(?:,|·|\||\/|;)\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function looksLikeYmd(token) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(token || '').trim());
}

/**
 * Upcoming date labels for a date-like USP entry (one label per day for rotation).
 * Passed / expired mega schedule days are omitted.
 */
function getUpcomingDateUspLabels(entry, pooja, lang) {
  const uspType = String(entry?.usp ?? entry?.uspType ?? entry?.usp_type ?? '').trim();
  if (!isDateLikeUspType(uspType)) return null;

  const rawValues = resolveUspValuesList(entry?.uspValues ?? entry?.usp_values, lang);
  let tokens = [];
  for (const value of rawValues) {
    tokens.push(...splitUspDateTokens(value));
  }

  const ymdTokens = tokens.filter(looksLikeYmd);
  if (ymdTokens.length > 0) {
    return ymdTokens
      .filter((ymd) => isMegaScheduleDateBookable(ymd, pooja) || String(ymd) >= getTodayString())
      .map((ymd) => formatYmdDayMonthShort(ymd, lang) || ymd)
      .filter(Boolean);
  }

  if (isMegaPooja(pooja)) {
    return getMegaPoojaScheduleFromToday(pooja)
      .map((row) => formatYmdDayMonthShort(row.date, lang))
      .filter(Boolean);
  }

  /* Non-YMD multi values: still rotate one-by-one (cannot reliably detect past). */
  if (tokens.length > 1) return tokens;
  return null;
}

/** Resolve display text for one entry in API `usps[]`. */
function resolvePoojaUspEntryDisplay(entry, pooja, lang, kshetraById = {}) {
  if (!entry) return '';
  const uspType = String(entry?.usp ?? entry?.uspType ?? entry?.usp_type ?? '').trim();
  const uspLower = uspType.toLowerCase();
  const fromValues = resolveUspValuesText(entry?.uspValues ?? entry?.usp_values, lang);
  if (fromValues) return fromValues;

  if (/kshetra/.test(uspLower)) {
    const kid = pooja?.kshetraId ?? entry?.kshetraId ?? entry?.kshetra_id;
    if (kid != null && kid !== '') return kshetraById[String(kid)] || '';
  }

  if (/address|venue|location/.test(uspLower)) {
    return getPoojaVenue(pooja, lang) || getLocalized(pooja?.address, lang) || '';
  }

  if (/thithi|tithi/.test(uspLower)) {
    return getLocalized(pooja?.thithiName, lang) || getLocalized(pooja?.tithiName, lang) || '';
  }

  if (/grace|daysofgrace/.test(uspLower)) {
    return getLocalized(pooja?.daysOfGraceName, lang) || '';
  }

  return '';
}

/**
 * Mega schedule → one USP row item per upcoming day (passed days omitted).
 * Uses `megaPoojaDetails.schedule[].date`.
 */
function getMegaScheduleUspItems(pooja, lang) {
  if (!isMegaPooja(pooja)) return [];
  return getMegaPoojaScheduleFromToday(pooja)
    .map((row) => {
      const ymd = String(row?.date || '').trim();
      if (!ymd) return null;
      const text = formatYmdDayMonthShort(ymd, lang);
      if (!text) return null;
      return { uspType: 'DATE', iconKind: 'tithi', text };
    })
    .filter(Boolean);
}

/**
 * All displayable USPs for a pooja card strip — from API `usps[]` or legacy single fields.
 * Mega poojas rotate each upcoming `megaPoojaDetails.schedule` date one-by-one
 * (passed dates are omitted). Date-like USPs expand the same way for non-mega.
 * @returns {{ uspType: string, iconKind: string, text: string }[]}
 */
export function getPoojaUspItems(pooja, lang, kshetraById = {}) {
  if (!pooja) return [];

  const items = [];
  const megaDateItems = getMegaScheduleUspItems(pooja, lang);
  let megaDatesAdded = false;

  const pushMegaDates = () => {
    if (megaDatesAdded || megaDateItems.length === 0) return;
    items.push(...megaDateItems);
    megaDatesAdded = true;
  };

  /* Mega: schedule dates first so the strip clearly rotates each upcoming day */
  pushMegaDates();

  const uspsArray = getPoojaUspsArray(pooja);
  if (uspsArray) {
    for (const entry of uspsArray) {
      const uspType = String(entry?.usp ?? entry?.uspType ?? entry?.usp_type ?? '').trim();
      const iconKind = getPoojaUspIconKindFromType(uspType, pooja);

      if (isDateLikeUspType(uspType)) {
        /* Mega dates already added from schedule — skip duplicate date USP */
        if (megaDatesAdded) continue;
        const dateLabels = getUpcomingDateUspLabels(entry, pooja, lang);
        if (dateLabels && dateLabels.length > 0) {
          for (const text of dateLabels) {
            items.push({ uspType, iconKind: 'tithi', text });
          }
          continue;
        }
        if (dateLabels && dateLabels.length === 0) continue;
      }

      const text = resolvePoojaUspEntryDisplay(entry, pooja, lang, kshetraById);
      if (!text) continue;
      items.push({
        uspType,
        iconKind,
        text,
      });
    }
  }

  /* Non-mega path may not have called this yet */
  pushMegaDates();

  if (items.length > 0) return items;

  const legacyText = getPoojaUspDisplayLegacy(pooja, lang, kshetraById);
  if (!legacyText) return [];

  return [
    {
      uspType: String(pooja?.usp ?? pooja?.usp_type ?? '').trim(),
      iconKind: getPoojaUspIconKindFromType(pooja?.usp ?? pooja?.usp_type, pooja),
      text: legacyText,
    },
  ];
}

/** Legacy single-usp resolution (uspValue / kshetra / address). */
function getPoojaUspDisplayLegacy(pooja, lang, kshetraById = {}) {
  const direct = getPoojaUspValue(pooja, lang);
  if (direct) return direct;

  const usp = String(pooja?.usp ?? '').trim().toLowerCase();

  if (usp.includes('kshetra')) {
    const kid = pooja?.kshetraId;
    if (kid != null && kid !== '') return kshetraById[String(kid)] || '';
  }

  if (usp === 'address' || usp === 'venue' || usp === 'location') {
    return getPoojaVenue(pooja, lang) || getLocalized(pooja?.address, lang) || '';
  }

  return '';
}

/** Map kshetra id → display name from GET /api/kshetras */
export function buildKshetraNameById(kshetras, lang) {
  const map = {};
  if (!Array.isArray(kshetras)) return map;
  for (const k of kshetras) {
    if (k?.id == null) continue;
    const name = getLocalized(k.name, lang) || (typeof k.name === 'string' ? k.name.trim() : '');
    if (name) map[String(k.id)] = name;
  }
  return map;
}

/**
 * Text for bottom image strip: first displayable USP, or resolved kshetra / address when applicable.
 * Returns '' when nothing to show.
 */
export function getPoojaUspDisplay(pooja, lang, kshetraById = {}) {
  const items = getPoojaUspItems(pooja, lang, kshetraById);
  return items[0]?.text || '';
}

/** Short description for card (first content or description, ~60 chars) */
export function getPoojaDescriptionSnippet(pooja, lang, maxLen = 60) {
  const shortDesc = stripHtml(getLocalized(pooja?.shortDescription, lang) || '');
  if (shortDesc) return shortDesc.length > maxLen ? shortDesc.slice(0, maxLen).trim() + '…' : shortDesc;

  const blocks = pooja?.contentBlocks || pooja?.blocks;
  if (blocks && Array.isArray(blocks) && blocks.length > 0) {
    const first = blocks[0];
    const text = stripHtml(getLocalized(first?.content ?? first?.text, lang) || getLocalized(first?.heading, lang) || '');
    if (text) return text.length > maxLen ? text.slice(0, maxLen).trim() + '…' : text;
  }
  const desc = pooja?.description;
  const text = stripHtml(getLocalized(desc, lang) || '');
  return text.length > maxLen ? text.slice(0, maxLen).trim() + '…' : text;
}

/** Get raw shortDescription if available */
export function getPoojaShortDescription(pooja, lang) {
  return getLocalized(pooja?.shortDescription, lang) || '';
}

/** Enrich cart item with full pooja data (images, title, etc.) when cart API returns minimal data */
export function enrichCartItemWithPooja(cartItem, allPoojas) {
  if (!cartItem || !Array.isArray(allPoojas) || allPoojas.length === 0) return cartItem;
  const poojaDetailsId = cartItem?.poojaDetailsId ?? cartItem?.poojaId;
  if (!poojaDetailsId) return cartItem;
  const pooja = allPoojas.find((p) => p.id === poojaDetailsId);
  if (!pooja) return cartItem;
  return { ...pooja, ...cartItem };
}

/** Get minimum price from participationFees (for listing cards), including variant amounts. */
export function getPoojaMinPrice(pooja) {
  const fees = getPoojaParticipationFees(pooja);
  if (fees.length === 0) return null;
  const amounts = [];
  for (const fee of fees) {
    if (participationFeeHasVariants(fee)) {
      for (const variant of getPackageVariants(fee)) {
        const n = Number(variant?.amount);
        if (!Number.isNaN(n)) amounts.push(n);
      }
    }
    const n = Number(fee?.amount);
    if (!Number.isNaN(n)) amounts.push(n);
  }
  return amounts.length > 0 ? Math.min(...amounts) : null;
}

/** True when this package has Individual / Couple / Family (or custom) variants. */
export function participationFeeHasVariants(fee) {
  return fee?.hasVariants === true && Array.isArray(fee?.variants) && fee.variants.length > 0;
}

export function getPackageVariants(fee) {
  return participationFeeHasVariants(fee) ? fee.variants : [];
}

/** Match `type.en` (initiate sends this string, not the map). */
export function getPackageVariantTypeEn(variant) {
  if (!variant) return '';
  if (typeof variant.type === 'string') return variant.type.trim();
  const fromMap = variant?.type?.en ?? variant?.typeEn ?? variant?.type_en;
  return String(fromMap || '').trim();
}

export function findPackageVariant(fee, variantType) {
  const list = getPackageVariants(fee);
  if (!list.length) return null;
  const want = String(variantType || '').trim();
  if (want) {
    const found = list.find((v) => getPackageVariantTypeEn(v) === want);
    if (found) return found;
  }
  const individual = list.find((v) => getPackageVariantTypeEn(v).toUpperCase() === 'INDIVIDUAL');
  return individual || list[0];
}

export function getDefaultPackageVariantType(fee) {
  const row = findPackageVariant(fee, 'INDIVIDUAL');
  return row ? getPackageVariantTypeEn(row) : '';
}

/** Short blurb for a package size (API text, else devotee count from min/max). */
export function getPackageVariantDescription(variant, lang = 'en') {
  if (!variant) return '';
  const raw =
    variant.shortDescription ??
    variant.short_description ??
    variant.description ??
    variant.desc;
  const localized = getLocalized(raw, lang);
  if (localized && String(localized).trim()) return String(localized).trim();
  if (typeof raw === 'string' && raw.trim()) return raw.trim();

  const minRaw = Number(variant?.minMembers ?? variant?.min_members);
  const maxRaw = Number(variant?.maxMembers ?? variant?.max_members);
  const min = Number.isFinite(minRaw) && minRaw > 0 ? minRaw : 0;
  const max = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : 0;
  if (min > 0 && max > 0) {
    if (min === max) {
      return min === 1 ? 'For 1 devotee' : `For ${min} devotees`;
    }
    return `For ${min}–${max} devotees`;
  }

  const type = getPackageVariantTypeEn(variant).toUpperCase();
  if (type === 'INDIVIDUAL') return 'For 1 devotee';
  if (type === 'COUPLE') return 'For 2 devotees';
  if (type === 'FAMILY') return 'For family participation';
  return '';
}

export function getParticipationFeeMemberLimits(fee, variantType) {
  if (participationFeeHasVariants(fee)) {
    const row = findPackageVariant(fee, variantType);
    return {
      min: Math.max(1, Number(row?.minMembers ?? row?.min_members) || 1),
      max: Math.max(1, Number(row?.maxMembers ?? row?.max_members) || 1),
    };
  }
  const min = Number(fee?.minMembers ?? fee?.minPersons ?? fee?.min_members);
  const max = Number(fee?.maxMembers ?? fee?.maxPersons ?? fee?.max_persons);
  return {
    min: Number.isFinite(min) && min > 0 ? min : 1,
    max: Number.isFinite(max) && max > 0 ? max : null,
  };
}

/** Participation fee tiers from API (`participationFees` or `participation_fees`). */
export function getPoojaParticipationFees(pooja) {
  const fees = pooja?.participationFees ?? pooja?.participation_fees;
  return Array.isArray(fees) ? fees : [];
}

/** Localized package label for a participation fee tier, or '' when missing. */
export function getParticipationFeeLabel(fee, lang = 'en') {
  if (!fee) return '';
  const localized = getLocalized(fee.label, lang);
  if (localized && String(localized).trim()) return String(localized).trim();
  if (typeof fee.label === 'string' && fee.label.trim()) return fee.label.trim();
  return '';
}

/**
 * Exact `participationFees[i].label.en` for POST /poojabookings/initiate.
 * Backend matches this string only — never a Hindi/Kannada title or a Figma heading.
 * Empty means omit `poojaLabel` and send `selectedPackageIndex` only.
 */
export function getParticipationFeeLabelEn(fee) {
  if (!fee) return '';
  const label = fee.label;
  if (label && typeof label === 'object' && !Array.isArray(label)) {
    const en = label.en;
    if (typeof en === 'string' && en.trim()) return en.trim();
  }
  return '';
}

/** Package short description from API (`shortDescription` / `short_description` / `description`). */
export function getParticipationFeeShortDescription(fee, lang = 'en') {
  if (!fee) return '';
  const raw = fee.shortDescription ?? fee.short_description ?? fee.description;
  const localized = getLocalized(raw, lang);
  if (localized && String(localized).trim()) return String(localized).trim();
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  return '';
}

/** Package image URL from API (`packageImageUrl` / snake_case / generic image fields). */
export function getParticipationFeeImageUrl(fee) {
  if (!fee) return '';
  const raw =
    fee.packageImageUrl ??
    fee.package_image_url ??
    fee.imageUrl ??
    fee.image_url ??
    fee.image;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : '';
}

/** Whether package amount should be shown (default true; honor explicit disable flags). */
export function isParticipationFeePriceEnabled(fee, pooja) {
  const flags = [
    fee?.showPrice,
    fee?.show_price,
    fee?.priceEnabled,
    fee?.price_enabled,
    fee?.pricesEnabled,
    fee?.prices_enabled,
    pooja?.showPackagePrice,
    pooja?.show_package_price,
    pooja?.showPrices,
    pooja?.show_prices,
    pooja?.pricesEnabled,
    pooja?.prices_enabled,
  ];
  for (const flag of flags) {
    if (flag === false || flag === 'false' || flag === 0 || flag === '0') return false;
  }
  return true;
}

/** Numeric package amount when present and price display is enabled. */
export function getParticipationFeeAmount(fee, pooja, variantType) {
  if (!fee || !isParticipationFeePriceEnabled(fee, pooja)) return null;
  if (participationFeeHasVariants(fee)) {
    const row = findPackageVariant(fee, variantType);
    const n = Number(row?.amount ?? fee.amount ?? fee.fee);
    return Number.isFinite(n) ? Math.round(n) : null;
  }
  const n = Number(fee.amount ?? fee.fee);
  return Number.isFinite(n) ? n : null;
}

/** True when pooja offers prasadam delivery (`prasadamAvailable`). */
export function isPoojaPrasadamAvailable(pooja) {
  return pooja?.prasadamAvailable === true || pooja?.prasadam_available === true;
}

/** Localized HTML bullets for prasadam opt-in details (`prasadaIncludedItems`). */
export function getPoojaPrasadaIncludedItems(pooja, lang = 'en') {
  if (!pooja) return [];
  return getLocalizedHtmlList(
    pooja.prasadaIncludedItems
      ?? pooja.prasadamIncludedItems
      ?? pooja.prasada_included_items
      ?? pooja.prasadam_included_items,
    lang,
  );
}

/** Prasadam delivery fee from API (`prasadamFee`), or 0 when missing/unavailable. */
export function getPoojaPrasadamFee(pooja) {
  if (!isPoojaPrasadamAvailable(pooja)) return 0;
  const n = Number(pooja?.prasadamFee ?? pooja?.prasadam_fee ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Stream rows saved on the pooja (`poojaStreamLinks` only).
 * A missing or empty list falls back to a single `poojaStreamLink` string.
 * `null` or `""` on that fallback means there is no stream. A saved row with an empty
 * `youtubeUrl` is kept so the page can show “Stay tuned”. `poojaLiveLink` is the live-page URL, not a player.
 * @returns {{ locationName: string, youtubeUrl: string, packageName: string }[]}
 */
export function resolvePoojaStreamRows(pooja) {
  if (!pooja || typeof pooja !== 'object') return [];
  const links = pooja.poojaStreamLinks;
  if (Array.isArray(links) && links.length > 0) {
    const rows = [];
    for (const row of links) {
      if (!row || typeof row !== 'object') continue;
      const youtubeUrl = typeof row.youtubeUrl === 'string' ? row.youtubeUrl.trim() : '';
      const packageName = typeof row.packageName === 'string' ? row.packageName.trim() : '';
      const locationName = typeof row.locationName === 'string' ? row.locationName.trim() : '';
      if (!youtubeUrl && !packageName && !locationName) continue;
      rows.push({ locationName, youtubeUrl, packageName });
    }
    return rows;
  }
  const fallback = typeof pooja.poojaStreamLink === 'string' ? pooja.poojaStreamLink.trim() : '';
  if (!fallback) return [];
  return [{ locationName: '', youtubeUrl: fallback, packageName: '' }];
}

/** Empty `packageName` is shown for every package. A label shows only for that English package. */
export function poojaStreamVisibleForPackage(row, selectedEnglishLabel) {
  const packageName = String(row?.packageName || '').trim();
  if (!packageName) return true;
  return packageName === String(selectedEnglishLabel || '').trim();
}

/** Participation fees that have a display label (required for booking card). */
export function getPoojaLabeledParticipationFees(pooja, lang = 'en') {
  return getPoojaParticipationFees(pooja).filter((fee) => getParticipationFeeLabel(fee, lang));
}

/** Merge pooja detail payloads (slug API + list fallback), keeping the richest fee tiers. */
export function mergePoojaDetailRecords(...records) {
  let merged = null;
  for (const record of records) {
    if (!record || typeof record !== 'object') continue;
    if (!merged) {
      merged = { ...record };
      continue;
    }
    const feesA = getPoojaParticipationFees(merged);
    const feesB = getPoojaParticipationFees(record);
    const bestFees = feesB.length > feesA.length ? feesB : feesA;
    const preserved = {};
    for (const key of POOJA_DETAIL_RICH_KEYS) {
      preserved[key] = pickRicherPoojaDetailField(merged[key], record[key]);
    }
    merged = {
      ...merged,
      ...record,
      ...preserved,
      ...(bestFees.length ? { participationFees: bestFees } : {}),
    };
  }
  return merged;
}

/** Get deity for filter/display – supports deity (single), deities (array) */
export function getPoojaDeity(pooja, lang) {
  const deities = pooja?.deities;
  if (Array.isArray(deities) && deities.length > 0) {
    const first = deities[0];
    const name = typeof first?.name === 'object' ? getLocalized(first.name, lang) : first?.name;
    return name || '';
  }
  return getLocalized(pooja?.deity, lang) || '';
}

const POOJA_SEARCH_LANGS = LANG_CODES;

function appendSearchTokens(parts, val, lang) {
  if (val == null) return;
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
    parts.push(String(val));
    return;
  }
  if (Array.isArray(val)) {
    val.forEach((item) => appendSearchTokens(parts, item, lang));
    return;
  }
  if (typeof val === 'object') {
    parts.push(getLocalized(val, lang));
    parts.push(getLocalized(val, 'en'));
  }
}

/**
 * Whether a pooja matches the listing search (pooja name, category, kshetra, deity, district/city/state, slug, tags, etc.).
 * @param {string} qLower - trimmed, lowercased query
 * @param {{ poojaCategories?: Array<{ id: *, name: * }> }} [opts] - pass categories from GET /api/pooja-categories so search matches category labels
 */
export function poojaMatchesSearchQuery(pooja, lang, qLower, opts = {}) {
  if (!qLower) return true;
  const { poojaCategories = [] } = opts;
  const parts = [];

  const t = pooja?.title;
  if (t && typeof t === 'object') {
    POOJA_SEARCH_LANGS.forEach((l) => {
      if (t[l]) parts.push(String(t[l]));
    });
  } else {
    appendSearchTokens(parts, t, lang);
  }
  parts.push(getPoojaTitle(pooja, lang));

  appendSearchTokens(parts, pooja?.shortDescription, lang);
  appendSearchTokens(parts, pooja?.description, lang);

  parts.push(getPoojaVenue(pooja, lang));
  appendSearchTokens(parts, pooja?.venue, lang);
  appendSearchTokens(parts, pooja?.location, lang);
  appendSearchTokens(parts, pooja?.address, lang);
  appendSearchTokens(parts, pooja?.uspValue, lang);

  appendSearchTokens(parts, pooja?.district, lang);
  appendSearchTokens(parts, pooja?.city, lang);
  appendSearchTokens(parts, pooja?.state, lang);
  if (pooja?.pincode != null) parts.push(String(pooja.pincode));
  appendSearchTokens(parts, pooja?.region, lang);

  const rawSlug = pooja?.slug;
  if (typeof rawSlug === 'string' && rawSlug.trim()) parts.push(rawSlug);
  POOJA_SEARCH_LANGS.forEach((l) => parts.push(getPoojaSlug(pooja, l)));

  appendSearchTokens(parts, pooja?.deity, lang);
  (pooja?.deities || []).forEach((d) => appendSearchTokens(parts, d?.name, lang));

  const k = pooja?.kshetra;
  if (k && typeof k === 'object') {
    appendSearchTokens(parts, k.name, lang);
    appendSearchTokens(parts, k.location, lang);
    appendSearchTokens(parts, k.city, lang);
    appendSearchTokens(parts, k.district, lang);
    appendSearchTokens(parts, k.state, lang);
    appendSearchTokens(parts, k.address, lang);
  }
  appendSearchTokens(parts, pooja?.kshetraName, lang);

  const catEmbed = pooja?.poojaCategory;
  if (catEmbed?.name) appendSearchTokens(parts, catEmbed.name, lang);
  const catId = pooja?.poojaCategoryId ?? pooja?.categoryId ?? catEmbed?.id ?? null;
  if (catId != null && poojaCategories.length > 0) {
    const row = poojaCategories.find((c) => String(c?.id) === String(catId));
    if (row?.name) appendSearchTokens(parts, row.name, lang);
  }

  (pooja?.tags || []).forEach((tag) => appendSearchTokens(parts, tag, lang));

  const pd = pooja?.poojaDetails;
  if (pd && typeof pd === 'object') {
    appendSearchTokens(parts, pd.venue, lang);
    appendSearchTokens(parts, pd.location, lang);
    appendSearchTokens(parts, pd.address, lang);
    appendSearchTokens(parts, pd.city, lang);
    appendSearchTokens(parts, pd.district, lang);
    appendSearchTokens(parts, pd.state, lang);
    const kpd = pd.kshetra;
    if (kpd && typeof kpd === 'object') {
      appendSearchTokens(parts, kpd.name, lang);
      appendSearchTokens(parts, kpd.location, lang);
      appendSearchTokens(parts, kpd.city, lang);
      appendSearchTokens(parts, kpd.district, lang);
      appendSearchTokens(parts, kpd.state, lang);
      appendSearchTokens(parts, kpd.address, lang);
    }
    appendSearchTokens(parts, pd.kshetraName, lang);
  }

  const haystack = parts.join(' ').toLowerCase().replace(/\s+/g, ' ');
  return haystackMatchesTokenizedQuery(haystack, qLower);
}

/**
 * Comma- or space-separated queries use AND: each term must appear (order-free).
 * So "Vindhyavasini, Vindhyachal" matches text that contains both substrings, not the literal comma phrase.
 */
export function haystackMatchesTokenizedQuery(haystackLower, qLower) {
  const hay = haystackLower;
  const q = (qLower || '').trim().toLowerCase();
  if (!q) return true;
  const tokens = q.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  if (tokens.length === 0) return true;
  const meaningful = tokens.filter((t) => t.length >= 2);
  if (meaningful.length === 0) return hay.includes(q);
  if (meaningful.length === 1) return hay.includes(meaningful[0]);
  return meaningful.every((t) => hay.includes(t));
}

/** Get deity ID for filter – supports deityId, deity.id, deities[0].id */
export function getPoojaDeityId(pooja) {
  const deities = pooja?.deities;
  if (Array.isArray(deities) && deities.length > 0 && deities[0]?.id) {
    return deities[0].id;
  }
  return pooja?.deityId ?? pooja?.deity?.id ?? null;
}

/** Extract unique deities from poojas for filter options */
export function getDeitiesFromPoojas(poojas) {
  const seen = new Map();
  (poojas || []).forEach((p) => {
    const arr = p?.deities;
    if (!Array.isArray(arr)) return;
    arr.forEach((d) => {
      const id = d?.id;
      const name = typeof d?.name === 'object' ? d?.name?.en ?? d?.name?.hi ?? Object.values(d?.name || {})[0] : d?.name;
      const nameStr = name ? String(name) : '';
      const key = id || nameStr;
      if (key && !seen.has(key)) {
        seen.set(key, { id: id || nameStr, name: nameStr || id || '' });
      }
    });
  });
  return Array.from(seen.values());
}

/** Check if pooja has any deity matching filter (by id or name, case-insensitive).
 *  Name may partially match so footer links like `?deity=Shiva` hit "Lord Shiva". */
export function poojaHasDeity(pooja, filterValue) {
  if (!filterValue || filterValue === 'all') return true;
  const fv = String(filterValue).toLowerCase();
  const deities = pooja?.deities;
  if (!Array.isArray(deities) || deities.length === 0) {
    const deityId = getPoojaDeityId(pooja);
    const deityName = getPoojaDeity(pooja, 'en');
    const nameLc = deityName ? String(deityName).toLowerCase() : '';
    return (
      deityId === filterValue
      || (nameLc && (nameLc === fv || nameLc.includes(fv) || (fv.length >= 4 && fv.includes(nameLc))))
    );
  }
  return deities.some((d) => {
    const id = d?.id;
    const name = typeof d?.name === 'object' ? d?.name?.en ?? Object.values(d?.name || {})[0] : d?.name;
    const nameStr = name ? String(name).toLowerCase() : '';
    return (
      id === filterValue
      || (nameStr && (nameStr === fv || nameStr.includes(fv) || (fv.length >= 4 && fv.includes(nameStr))))
    );
  });
}

/** Get pooja category ID for filter */
export function getPoojaCategoryId(pooja) {
  return pooja?.poojaCategoryId ?? pooja?.categoryId ?? pooja?.poojaCategory?.id ?? null;
}

/** Tithi filter id from pooja (`thithiId` is API spelling). */
export function getPoojaThithiId(pooja) {
  const v = pooja?.thithiId ?? pooja?.tithiId ?? pooja?.tithi?.id;
  if (v == null || v === '') return null;
  return String(v);
}

/** Days of grace filter id from pooja. */
export function getPoojaDaysOfGraceId(pooja) {
  const v =
    pooja?.daysOfGrace ??
    pooja?.daysOfGraceId ??
    pooja?.dayOfGraceId ??
    pooja?.daysOfGrace?.id;
  if (v == null || v === '') return null;
  if (typeof v === 'object' && v?.id) return String(v.id);
  return String(v);
}

function parsePositiveDurationNumber(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** `duration` field is sometimes hours (e.g. 2.5); minute fields are always minutes. */
function durationFieldToMinutes(raw) {
  const n = parsePositiveDurationNumber(raw);
  if (n == null) return null;
  const isFractional = Math.abs(n - Math.round(n)) > 0.001;
  if (isFractional && n < 48) return Math.round(n * 60);
  if (n <= 12 && Number.isInteger(n)) return n * 60;
  return Math.round(n);
}

/** Get duration in minutes (from API if available) */
export function getPoojaDurationMinutes(pooja) {
  const minuteRaw = pooja?.poojaMinutes ?? pooja?.durationMinutes;
  const fromMinuteField = parsePositiveDurationNumber(minuteRaw);
  if (fromMinuteField != null) return Math.round(fromMinuteField);

  if (pooja?.duration != null) return durationFieldToMinutes(pooja.duration);
  return null;
}

/** Format duration for display e.g. "1 min", "1 hr", "2.5 hrs" */
export function formatPoojaDuration(pooja) {
  const mins = getPoojaDurationMinutes(pooja);
  if (mins == null || mins <= 0) return '';

  if (mins < 60) {
    return mins === 1 ? '1 min' : `${mins} min`;
  }

  const hrs = Number((Math.round((mins / 60) * 10) / 10).toFixed(1));
  const wholeHrs = Math.round(hrs);
  if (Math.abs(hrs - wholeHrs) < 0.05) {
    return wholeHrs === 1 ? '1 hr' : `${wholeHrs} hr`;
  }
  return `${hrs} hrs`;
}
